'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { DEFAULT_LOCALE, LOCALE_COOKIE_NAME, isLocale, type Locale } from '@/lib/i18n';
export type { Locale } from '@/lib/i18n';
interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}
const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Helper function to set cookie
function setCookie(name: string, value: string, days: number = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}
function getBrowserLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const browserLang = navigator.language || (navigator as Navigator & {
    userLanguage?: string;
  }).userLanguage || DEFAULT_LOCALE;
  if (browserLang.startsWith('tr')) return 'tr';
  if (browserLang.startsWith('de')) return 'de';
  return DEFAULT_LOCALE;
}
function getClientLocale(): Locale {
  const cookieLocale = getCookie(LOCALE_COOKIE_NAME);
  if (isLocale(cookieLocale)) {
    return cookieLocale;
  }
  return getBrowserLocale();
}
interface I18nProviderProps {
  children: ReactNode;
  translations: Record<Locale, Record<string, unknown>>;
  initialLocale?: Locale;
}
export function I18nProvider(t0) {
  const $ = _c(16);
  const {
    children,
    translations,
    initialLocale: t1
  } = t0;
  const initialLocale = t1 === undefined ? DEFAULT_LOCALE : t1;
  const [locale, setLocaleState] = useState(initialLocale);
  let t2;
  let t3;
  if ($[0] !== initialLocale) {
    t2 = () => {
      const nextLocale = getClientLocale();
      if (nextLocale !== initialLocale) {
        const frameId = requestAnimationFrame(() => {
          setLocaleState(nextLocale);
        });
        return () => cancelAnimationFrame(frameId);
      }
    };
    t3 = [initialLocale];
    $[0] = initialLocale;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== locale) {
    t4 = () => {
      setCookie(LOCALE_COOKIE_NAME, locale);
      if (typeof document !== "undefined") {
        document.documentElement.lang = locale;
      }
    };
    t5 = [locale];
    $[3] = locale;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = newLocale => {
      setLocaleState(newLocale);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const setLocale = t6;
  let t7;
  if ($[7] !== locale || $[8] !== translations) {
    t7 = (key, params) => {
      const keys = key.split(".");
      let value = translations[locale];
      for (const k of keys) {
        if (value && typeof value === "object" && value !== null && k in value) {
          value = (value as Record<string, unknown>)[k];
        } else {
          value = translations[DEFAULT_LOCALE];
          for (const fallbackKey of keys) {
            if (value && typeof value === "object" && value !== null && fallbackKey in value) {
              value = (value as Record<string, unknown>)[fallbackKey];
            } else {
              return key;
            }
          }
          break;
        }
      }
      if (typeof value !== "string") {
        return key;
      }
      if (params) {
        return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => params[paramKey]?.toString() || match);
      }
      return value;
    };
    $[7] = locale;
    $[8] = translations;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t = t7;
  let t8;
  if ($[10] !== locale || $[11] !== t) {
    t8 = {
      locale,
      setLocale,
      t
    };
    $[10] = locale;
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== children || $[14] !== t8) {
    t9 = <I18nContext.Provider value={t8}>{children}</I18nContext.Provider>;
    $[13] = children;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
export function useTranslation() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }
  return context;
}